import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { StrawberryTableBody } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTableBody';
import { StrawberryTableHeader } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTableHeader';
import { tableLineHeight } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { rem } from '@mantine/core';
import { memo, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { IBodyProps } from './StrawberryTableBody.types';
import { IHeaderProps } from './StrawberryTableHeader.types';

interface Props {
	className?: string;
	header: IHeaderProps['content'][];
	body: IBodyProps['content'][];
	hasFirstLeftVerticalLine?: boolean;
	hasUpperHorizontalLine?: boolean;
	hasBottomHorizontalLine?: boolean;
	selectedPaddingForVerticalSeparator?: number;
	hideFirstHeaderHorizontalLine?: boolean;
}

const Table = styled.table``;

const StrawberryTable = ({ className, header, body, hasFirstLeftVerticalLine }: Props) => {
	const [verticalSeparatorHeight, setVerticalSeparatorHeight] = useState('0px');
	const tableRef = useRef<HTMLTableElement>(null);

	useEffect(() => {
		const resizeObserver = new ResizeObserver(() => {
			if (tableRef.current?.clientHeight) {
				const tableHeight = tableRef.current.clientHeight;

				setVerticalSeparatorHeight(
					`calc(${tableHeight}px - (32px + var(--border-width) * 3))`
				);
			}
		});

		tableRef.current && resizeObserver.observe(tableRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const isBodyArrayOfArrays = body.length > 0 ? Array.isArray(body[0]) : false;

	return (
		<Table className={className} ref={tableRef}>
			<thead>
				<tr>
					{header.map((content: IHeaderProps['content'], index: number) => (
						<StrawberryTableHeader
							key={index}
							content={content}
							height={verticalSeparatorHeight}
							index={index}
							hasFirstLeftVerticalLine={hasFirstLeftVerticalLine}
						/>
					))}
				</tr>
			</thead>
			<tbody>
				{isBodyArrayOfArrays ? (
					body.map((content, index: number) => {
						return (
							<tr key={index}>
								<StrawberryTableBody key={index} content={content} />
							</tr>
						);
					})
				) : (
					<tr>
						{body.map((content, index: number) => {
							return <StrawberryTableBody key={index} content={content} />;
						})}
					</tr>
				)}
			</tbody>
		</Table>
	);
};

const Memoized = memo(StrawberryTable);
const Styled = styled(Memoized)`
	width: 100%;
	--background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	--hover-background-color: ${({ theme }) => theme.v2.color.neutral[300]};
	--font-color: ${({ theme }) => theme.v2.color.neutral[900]};
	--border-color: ${({ theme }) => theme.v2.color.neutral[300]};
	--border-radius: ${rem(8)};
	--hover-border-radius: ${rem(4)};
	--border-width: ${rem(1)};
	--adjusted-padding: ${({ selectedPaddingForVerticalSeparator }) =>
		selectedPaddingForVerticalSeparator
			? `${rem(selectedPaddingForVerticalSeparator)}`
			: `1rem`};
	color: var(--font-color);
	text-align: left;
	background-color: var(--background-color);
	border-radius: var(--border-radius);
	border: var(--border-width) solid transparent;
	border-collapse: separate;

	tr {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		flex-grow: 1;
		width: 100%;
	}

	tbody tr:not(:last-child) {
		td ${Text} {
			border-bottom: var(--border-width) solid var(--border-color);
		}
	}

	${({ hideFirstHeaderHorizontalLine }) =>
		hideFirstHeaderHorizontalLine
			? css`
					thead th:not(:first-child) {
						${Text} {
							border-bottom: var(--border-width) solid var(--border-color);
						}
					}
				`
			: css`
					thead th {
						${Text} {
							border-bottom: var(--border-width) solid var(--border-color);
						}
					}
				`}

	th {
		display: flex;
		flex-direction: row;
		position: relative;
		margin: 0 1rem;
		align-items: flex-end;

		${Text} {
			gap: ${rem(4)};
			line-height: ${tableLineHeight};
			word-break: break-word;
			padding-bottom: ${rem(4)};
			width: 11.75rem;
		}

		:first-child {
			margin-left: 0;
		}
	}

	td {
		width: 11.75rem;
		margin: ${rem(4)} 1rem 0 1rem;

		:first-child {
			margin-left: 0;
		}

		${Text} {
			gap: ${rem(4)};
			line-height: ${tableLineHeight};
			word-break: break-word;
			padding-bottom: ${rem(4)};
			width: 11.75rem;
		}
	}

	thead {
		tr {
			${({ hasUpperHorizontalLine }) => {
				if (hasUpperHorizontalLine) {
					return `
                border-top: var(--border-width) solid var(--border-color);

 `;
				}
				return;
			}}

			padding-top: var(--adjusted-padding);
		}
	}

	tbody {
		display: block;
		${({ hasBottomHorizontalLine }) => {
			if (hasBottomHorizontalLine) {
				return `
                border-bottom: var(--border-width) solid var(--border-color);
 `;
			}
			return;
		}}

		padding-bottom: var(--adjusted-padding);
	}
`;

export { Styled as StrawberryTable };
